import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import EmptyState from '../../components/common/EmptyState';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import EventForm from './EventForm';
import InviteEmailModal from './InviteEmailModal';
import AttendanceModal from './AttendanceModal';
import useContextStore, { selectCurrentContext, scopeOfContext } from '../../store/useContextStore';
import useEventsStore from '../../store/useEventsStore';
import useSignupsStore, { signupCounts, fmtFilled } from '../../store/useSignupsStore';
import eventsService from '../../api/services/events.service';
import noSchoolService from '../../api/services/noSchool.service';
import { showToast } from '../../store/useToastStore';
import { fmtMonthDay, fmtEventTime, fmtNoSchoolDates } from '../../utils/formatters';
import { IconTrash } from '../../components/icons';
import { PATHS } from '../../routes/navConfig';

const PAGE_SIZE = 10;

const byDate = (a, b) => a.date.localeCompare(b.date);
const fail = (err) => showToast(err.message);

export default function CalendarPage() {
  const ctx = useContextStore(selectCurrentContext);
  const {
    events, eventsLoading, eventsTotal, eventsTotalPages, loadEvents, updateEvent, deleteEvent,
    noSchoolDays, removeNoSchoolDay,
  } = useEventsStore();
  const { signups, loadSignups } = useSignupsStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [formOpen, setFormOpen] = useState(() => !!location.state?.openEventForm);
  const [formKey, setFormKey] = useState(0);
  const [editingEvent, setEditingEvent] = useState(null);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [attendEventId, setAttendEventId] = useState(null);
  const [page, setPage] = useState(1);

  const isMaster = ctx.type === 'master';
  const scope = scopeOfContext(ctx);
  const networkId = ctx?.id || null;

  useEffect(() => {
    setPage(1);
  }, [networkId]);

  /* one server page at a time — the list unions the school's days off in with
     the network's events, so both kinds reach the store from this one call */
  useEffect(() => {
    if (!networkId) return;
    loadEvents(networkId, { page, limit: PAGE_SIZE }, scope).catch(fail);
  }, [networkId, page, scope, loadEvents]);

  /* the sign-ups linked to these events drive each row's volunteer chip */
  useEffect(() => {
    if (!networkId) return;
    loadSignups(networkId, { page: 1, limit: 100 }, scope).catch(() => {});
  }, [networkId, scope, loadSignups]);

  const refetch = () => loadEvents(networkId, { page, limit: PAGE_SIZE }, scope).catch(fail);

  /* this page's events and days off in one list, by date */
  const rows = [...events.filter((e) => e.scope === scope), ...noSchoolDays].sort(byDate);
  /* the server reports 0 pages for an empty calendar */
  const totalPages = Math.max(1, eventsTotalPages);

  /* a delete can empty the last page — step back onto a real one */
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const newEvent = () => {
    setEditingEvent(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };

  const editEvent = (ev) => {
    setEditingEvent(ev);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  };

  const closeForm = () => {
    setEditingEvent(null);
    setFormKey((k) => k + 1);
    setFormOpen(false);
  };

  const onFormSaved = () => {
    closeForm();
    refetch();
  };

  /* Delete = custom confirm modal, then the API for backend records, then
     refetch. A day off is a note, so it goes through its own API. */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deletingDayOff = deleteTarget?.mode === 'noSchool';
  const confirmDelete = async () => {
    const ev = deleteTarget;
    if (!ev) return;
    const isDayOff = ev.mode === 'noSchool';
    if (typeof ev.id === 'string' && ev.id) {
      setDeleting(true);
      try {
        if (isDayOff) await noSchoolService.remove(networkId, ev.id);
        else await eventsService.deleteEvent(ev.id);
      } catch (err) {
        showToast(err.message);
        setDeleting(false);
        return;
      }
      setDeleting(false);
    }
    if (isDayOff) removeNoSchoolDay(ev.id);
    else deleteEvent(ev.id);
    setDeleteTarget(null);
    showToast(isDayOff ? 'No school day deleted ✓' : 'Event deleted ✓');
    refetch();
  };

  /* Send to Parents = flip the event's status true; the row then shows the going count. */
  const [sendingId, setSendingId] = useState(null);
  const sendToParents = async (ev) => {
    if (typeof ev.id === 'string' && ev.id) {
      setSendingId(ev.id);
      try {
        await eventsService.setEventStatus(ev.id, true);
      } catch (err) {
        showToast(err.message);
        setSendingId(null);
        return;
      }
      setSendingId(null);
    }
    updateEvent(ev.id, { status: true });
    showToast('Event sent to parents ✓');
  };

  return (
    <div className="sec on">
      <SectionHead
        title={isMaster ? 'Master Calendar' : 'Calendar'}
        sub={
          isMaster
            ? `School-wide events for all of ${ctx.schoolName || ctx.name}.`
            : `Events just for ${ctx.name} — load up the year now, send each one when you're ready.`
        }
        actions={<button className="btn btn-primary" onClick={newEvent}>+ Add Event</button>}
      />

      <EventForm key={formKey} open={formOpen} editingEvent={editingEvent} onCancel={closeForm} onSaved={onFormSaved} />

      <Card>
        <div>
          {eventsLoading && (
            <div className="hint" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '18px 0' }}>
              <Spinner variant="green" /> Loading events…
            </div>
          )}
          {!eventsLoading && rows.length === 0 && <EmptyState>No events yet — add your first one.</EmptyState>}
          {!eventsLoading &&
            rows.map((ev) => {
              const md = fmtMonthDay(ev.date);

              /* A day off (Master Calendar only) sends nothing and has no public
                 page: 🚫 + title, the date or range, a red chip, and no send
                 button or copy link. The inert "No Email Needed" keeps the slot
                 every other row has. */
              if (ev.mode === 'noSchool') {
                return (
                  <div className="ev-row" key={`ns-${ev.id}`}>
                    <div className="ev-date-badge lg">
                      <div className="mo">{md.mo}</div>
                      <div className="dy">{md.dy}</div>
                    </div>
                    <div className="ev-body">
                      <div className="t">🚫 {ev.title}</div>
                      <div className="d">No school for every class · {fmtNoSchoolDates(ev.date, ev.endDate)}</div>
                    </div>
                    <div className="ev-actions">
                      <span className="ev-status noschool">No school</span>
                      <button className="btn btn-ghost btn-sm" disabled style={{ opacity: 0.5, cursor: 'default' }}>
                        No Email Needed
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => editEvent(ev)}>Edit</button>
                      <div className="ev-trash" title="Delete no school day" onClick={() => setDeleteTarget(ev)}>
                        <IconTrash />
                      </div>
                    </div>
                  </div>
                );
              }

              const timeStr = fmtEventTime(ev.startTime, ev.endTime);
              const linkedSu = signups.find((s) => s.eventId === ev.id) || null;
              const counts = linkedSu ? signupCounts(linkedSu) : null;

              return (
                <div className="ev-row" key={`ev-${ev.id}`}>
                  <div className="ev-date-badge lg">
                    <div className="mo">{md.mo}</div>
                    <div className="dy">{md.dy}</div>
                  </div>
                  {ev.image && <img className="ev-thumb" src={ev.image} alt="" />}
                  <div className="ev-body">
                    <div className="t">
                      {ev.title}
                      {timeStr && <span className="tm"> · {timeStr}</span>}
                    </div>
                    {ev.locationName && <div className="d">{ev.locationName}</div>}
                    <div className="d">{ev.desc}</div>
                  </div>
                  <div className="ev-actions">
                    {/* isEmailSent drives the chip; status true = already sent to parents */}
                    <span className={`ev-status ${ev.isEmailSent ? 'published' : 'draft'}`}>
                      {ev.isEmailSent ? 'Invitation sent' : 'Not sent yet'}
                    </span>
                    {linkedSu && (
                      <span
                        className="src-chip"
                        style={{ cursor: 'pointer', background: 'var(--amber-bg)', color: 'var(--amber)' }}
                        title="View linked sign-up"
                        onClick={() => navigate(PATHS.SIGNUPS, { state: { view: 'detail', signupId: linkedSu.id } })}
                      >
                        {fmtFilled(counts)}
                      </span>
                    )}
                    {ev.status ? (
                      <span
                        className="src-chip"
                        style={{ cursor: 'pointer' }}
                        title="View attendance"
                        onClick={() => setAttendEventId(ev.id)}
                      >
                        {ev.going || 0} going
                      </span>
                    ) : (
                      <button className="btn btn-primary btn-sm" disabled={sendingId === ev.id} onClick={() => sendToParents(ev)}>
                        {sendingId === ev.id ? 'Sending…' : 'Send to Parents'}
                      </button>
                    )}
                    {!ev.isEmailSent && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setPendingInvite({ eventId: ev.id, preferSchedule: true })}>
                        Schedule Email
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => editEvent(ev)}>Edit</button>
                    <div className="ev-trash" title="Delete event" onClick={() => setDeleteTarget(ev)}>
                      <IconTrash />
                    </div>
                  </div>
                </div>
              );
            })}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                {eventsTotal} on the calendar · Page {page} of {totalPages}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" disabled={page <= 1 || eventsLoading} onClick={() => setPage((p) => p - 1)}>
                  ← Prev
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page >= totalPages || eventsLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <InviteEmailModal pending={pendingInvite} onClose={() => setPendingInvite(null)} />
      <AttendanceModal eventId={attendEventId} onClose={() => setAttendEventId(null)} />

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={deletingDayOff ? 'Delete No School Day' : 'Delete Event'}
        maxWidth={420}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button
              className="btn"
              style={{ background: 'var(--red)', color: '#fff' }}
              disabled={deleting}
              onClick={confirmDelete}
            >
              {deleting ? 'Deleting…' : deletingDayOff ? 'Delete No School Day' : 'Delete Event'}
            </button>
          </>
        }
      >
        {deleteTarget && (
          <>
            <div className="ev-row" style={{ padding: 0, border: 'none', marginBottom: 12 }}>
              <div className="ev-date-badge lg">
                <div className="mo">{fmtMonthDay(deleteTarget.date).mo}</div>
                <div className="dy">{fmtMonthDay(deleteTarget.date).dy}</div>
              </div>
              {deleteTarget.image && <img className="ev-thumb" src={deleteTarget.image} alt="" />}
              <div className="ev-body">
                <div className="t">{deletingDayOff ? `🚫 ${deleteTarget.title}` : deleteTarget.title}</div>
                {deletingDayOff && <div className="d">{fmtNoSchoolDates(deleteTarget.date, deleteTarget.endDate)}</div>}
                {deleteTarget.locationName && <div className="d">{deleteTarget.locationName}</div>}
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
              {deletingDayOff
                ? "This takes the day off every class calendar in Sprout — parents will no longer see it. This can't be undone."
                : "This removes the event for everyone in the class — parents will no longer see it in the app. This can't be undone."}
            </p>
          </>
        )}
      </Modal>
    </div>
  );
}
